import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  NavLink,
  Link,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { User, LogOut } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import Resources from './components/Resources';
import Home from './components/Home';
import Problems from './components/Problems';
import ProblemDetail from './components/ProblemDetail';
import Auth from './components/Auth';
import QuantumSimulator from './components/QuantumSimulator';
import ProtectedRoute from './components/ProtectedRoute';
import HintChatbot from './components/HintChatbot';
import { HintEditorProvider } from './context/HintEditorContext';
import './App.css';

function AppShell({
  user,
  showAuth,
  onSignIn,
  onSignOut,
  onCloseAuth,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user && location.state?.from) {
      navigate(location.state.from, { replace: true, state: {} });
    }
  }, [user, location.state?.from, navigate]);

  return (
    <div className="App">
      <nav className="navbar">
        <Link to={user ? "/problems" : "/"} className="nav-brand">
          <img src="/logo.png" alt="QubitCompile" className="nav-icon" />
          <span>ubitCompile</span>
        </Link>

        <div className="nav-links">
          <div className="nav-primary-links">
            {!user && (
              <NavLink to="/" end className="nav-link">
                <span>Home</span>
              </NavLink>
            )}
            {user && (
              <>
                <NavLink to="/problems" className="nav-link">
                  <span>Problems</span>
                </NavLink>
                <NavLink to="/resources" className="nav-link">
                  <span>Resources</span>
                </NavLink>
                <NavLink to="/simulator" className="nav-link">
                  <span>Quantum Simulator</span>
                </NavLink>
              </>
            )}
          </div>
          <div className="nav-actions">
            {user ? (
              <div className="user-menu">
                <div className="user-info">
                  {user.photoURL && (
                    <img src={user.photoURL} alt="Profile" className="user-avatar" />
                  )}
                  <span className="user-name">{user.displayName || user.email}</span>
                </div>
                <button className="sign-out-btn" onClick={onSignOut}>
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <button type="button" className="sign-in-btn" onClick={onSignIn}>
                <User size={18} strokeWidth={2} />
                <span>Sign In</span>
              </button>
            )}
          </div>
        </div>
      </nav>

      <main className="main-content">
        <Routes>
          <Route
            path="/"
            element={user ? <Navigate to="/problems" replace /> : <Home user={user} />}
          />
          <Route
            path="/resources"
            element={
              <ProtectedRoute user={user} onRequireAuth={onSignIn}>
                <Resources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/problems"
            element={
              <ProtectedRoute user={user} onRequireAuth={onSignIn}>
                <Problems />
              </ProtectedRoute>
            }
          />
          <Route
            path="/problems/:id"
            element={
              <ProtectedRoute user={user} onRequireAuth={onSignIn}>
                <ProblemDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/simulator"
            element={
              <ProtectedRoute user={user} onRequireAuth={onSignIn}>
                <QuantumSimulator />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>

      {showAuth && (
        <div className="auth-modal-overlay" onClick={onCloseAuth}>
          <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
            <Auth onClose={onCloseAuth} />
          </div>
        </div>
      )}

      <HintChatbot user={user} />
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    // Listen for custom event to show auth modal
    const handleShowAuthModal = () => {
      setShowAuth(true);
    };

    window.addEventListener('showAuthModal', handleShowAuthModal);

    return () => {
      unsubscribe();
      window.removeEventListener('showAuthModal', handleShowAuthModal);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSignIn = () => {
    setShowAuth(true);
  };

  const handleCloseAuth = () => {
    setShowAuth(false);
  };

  if (loading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          background: '#f5f6f8'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: '1rem',
            color: '#1d1d1f'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid rgba(0, 0, 0, 0.1)',
              borderTop: '3px solid #6b8a9a',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <HintEditorProvider>
        <AppShell
          user={user}
          showAuth={showAuth}
          onSignIn={handleSignIn}
          onSignOut={handleSignOut}
          onCloseAuth={handleCloseAuth}
        />
      </HintEditorProvider>
    </Router>
  );
}

export default App;